import styles from "./ScreenshotVisualButton.module.css";
import { HoverPopover } from "src/components/ui-components/HoverPopover/HoverPopover";
import { Tooltip } from "src/components/ui-components/Tooltip/Tooltip";
import { IconButton } from "src/components/ui-components/Button/IconButton";
import { IconAnchorButton } from "src/components/ui-components/Button/IconAnchorButton";
import { IconCamera } from "src/components/ui-components/Icons/IconCamera";
import { IconCopy } from "src/components/ui-components/Icons/IconCopy";
import { IconDownloadSimple } from "src/components/ui-components/Icons/IconDownloadSimple";
import { IconLoading } from "src/components/ui-components/Icons/IconLoading";
import { IconCheckmark } from "src/components/ui-components/Icons/IconCheckmark";

import { useState, useEffect } from "react";
import {
  copyPngScreenshotToClipboard,
  savePngScreenshot,
} from "src/utils/takePngScreenshot";
import { addToastNotification } from "shared/Toasts/toastNotificationStore";
import { useSession } from "src/components/session/session";
import { useLoginUrl } from "src/hooks/useLoginUrl";
import AmplitudeService from "src/services/amplitude";
import * as Sentry from "@sentry/react";

interface Props {
  visualElement: HTMLElement;

  queryId: number;
  visualizationId: number;
}

export function ScreenshotVisualButton(props: Props) {
  const { visualElement, queryId, visualizationId } = props;

  const session = useSession();
  const loginUrl = useLoginUrl();

  const [state, setState] = useState<
    | { status: "idle" | "loading"; message?: undefined }
    | { status: "success"; message: string }
  >({ status: "idle" });

  useEffect(() => {
    if (state.status === "success") {
      setTimeout(() => setState({ status: "idle" }), 1000);
    }
  }, [state]);

  if (!session) {
    return (
      <Tooltip label="Log in to take screenshot" position="above-center">
        <IconAnchorButton
          className={styles.screenshotButton}
          theme="ghost"
          size="XS"
          href={loginUrl}
          aria-label="Log in to take visualization screenshot"
        >
          <IconCamera />
        </IconAnchorButton>
      </Tooltip>
    );
  }

  if (state.status === "loading") {
    return (
      <IconButton
        className={styles.screenshotButton}
        theme="ghost"
        size="XS"
        loading
      >
        <IconLoading />
      </IconButton>
    );
  }

  if (state.status === "success") {
    return (
      <Tooltip label={state.message} position="below-center" forceOpen>
        <IconButton
          className={styles.screenshotButton}
          theme="ghost"
          size="XS"
          aria-label={state.message}
        >
          <IconCheckmark />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <HoverPopover
      position="cover-anchor-top-right"
      content={({ close }) => (
        <div className={styles.buttonGroup}>
          <Tooltip label="Copy screenshot to clipboard" position="above-center">
            <IconButton
              className={styles.screenshotButton}
              theme="primary"
              size="XS"
              onClick={() => {
                setState({ status: "loading" });
                copyScreenshot(visualElement, {
                  queryId,
                  visualizationId,
                }).then(
                  () => setState({ status: "success", message: "Copied!" }),
                  () => setState({ status: "idle" })
                );
              }}
              aria-label="Copy visualization screenshot to clipboard"
            >
              <IconCopy />
            </IconButton>
          </Tooltip>

          <Tooltip label="Save screenshot" position="above-center">
            <IconButton
              className={styles.screenshotButton}
              theme="primary"
              size="XS"
              onClick={() => {
                close();
                setState({ status: "loading" });
                saveScreenshot(visualElement, {
                  queryId,
                  visualizationId,
                }).then(
                  () => setState({ status: "success", message: "Saved!" }),
                  () => setState({ status: "idle" })
                );
              }}
              aria-label="Save visualization screenshot"
            >
              <IconDownloadSimple />
            </IconButton>
          </Tooltip>
        </div>
      )}
      wrapperClassName={styles.hoverPopoverWrapper}
      renderInPortal
    >
      <IconButton
        className={styles.screenshotButton}
        theme="ghost"
        size="XS"
        aria-label="Take visualization screenshot"
      >
        <IconCamera />
      </IconButton>
    </HoverPopover>
  );
}

export const CLASSNAME_HIDE_IN_SCREENSHOTS = "hide-in-screenshots";

function copyScreenshot(
  element: HTMLElement,
  { queryId, visualizationId }: { queryId: number; visualizationId: number }
) {
  return copyPngScreenshotToClipboard(element, {
    domtoimageOptions: {
      scale: 2,
      filter(node) {
        return !node.classList?.contains(CLASSNAME_HIDE_IN_SCREENSHOTS);
      },
    },
  }).then(
    () => {
      AmplitudeService.trackEvent("Visualization Screenshot Copied", {
        query_id: queryId,
        visualization_id: visualizationId,
      });
    },
    (error) => {
      if (error instanceof Error) {
        error.message = "Copy visualization screenshot: " + error.message;
      }
      Sentry.captureException(error, {
        extra: {
          queryId,
          visualizationId,
        },
      });

      addToastNotification({
        level: "error",
        title: "Could not copy screenshot",
        description: (
          <>
            Something went wrong when trying to copy screenshot of
            visualization.
          </>
        ),
      });

      throw error;
    }
  );
}

function saveScreenshot(
  element: HTMLElement,
  { queryId, visualizationId }: { queryId: number; visualizationId: number }
) {
  return savePngScreenshot(element, {
    domtoimageOptions: {
      scale: 2,
      filter(node) {
        return !node.classList?.contains(CLASSNAME_HIDE_IN_SCREENSHOTS);
      },
    },
  }).then(
    () => {
      AmplitudeService.trackEvent("Visualization Screenshot Saved", {
        query_id: queryId,
        visualization_id: visualizationId,
      });
    },
    (error) => {
      if (error instanceof Error) {
        error.message = "Save visualization screenshot: " + error.message;
      }
      Sentry.captureException(error, {
        extra: {
          queryId,
          visualizationId,
        },
      });

      addToastNotification({
        level: "error",
        title: "Could not save screenshot",
        description: (
          <>
            Something went wrong when trying to save screenshot of
            visualization.
          </>
        ),
      });

      throw error;
    }
  );
}
